import React, { useState } from 'react';
import { Card, Button, Dropdown } from 'antd';
import {
  EyeOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  FileOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
  VideoCameraOutlined,
  FileAddOutlined,
  FolderAddOutlined,
} from '@ant-design/icons';
import PDFThumbnail from '../common/PDFThumbnail';
import FilePreviewModal from '../common/FilePreviewModal';
import TemplateSelector from '../markdown/TemplateSelector';
import DocumentModal from '../markdown/DocumentModal';
import useProposalPreviewStyles from '../../styles/proposal/ProposalPreview';
import likeIcon from '../../styles/images/like-close.png';
import likeFilledIcon from '../../styles/images/like-filled.png';
import commentIcon from '../../styles/images/comment-icon.png';
import attachmentService from '../../services/attachmentService';
import { markdownToFile, generateDocumentFilename, blobToMarkdown } from '../../utils/markdownUtils';
import uploadService from '../../services/uploadService';
import useAppMessage from '../../hooks/useAppMessage';
import { useEmbeddedAuth } from '../../contexts/EmbeddedAuthContext';
import { getCurrentUser } from '../../services/authService';


const ProposalPreview = ({
  commentsPagination,
  previewContent,
  allItems,
  selectedItem,
  setSelectedItem,
  getCurrentItem,
  getIcon,
  getTypeName,
  renderPreview,
  styles,
  scrollContainerRef,
  showLeftScroll,
  showRightScroll,
  scrollLeft,
  scrollRight,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseLeave,
  isDragging,
  setCommentModalVisible,
  optimizeMode,
  onFileUpload, // 新增：文件上传回调
  onFileUpdate, // 新增：文件更新回调
  isEditMode = false, // 新增：是否为编辑模式
  onFileDelete, // 新增：文件删除回调
  currentStep = 1,
  // 新增：点赞和评论相关props
  proposalData = null, // 提案数据，包含点赞状态和数量
  onLike = null, // 点赞回调函数
  likeLoading = false, // 点赞加载状态
  isSubmitting = false, // 新增：是否正在提交
}) => {
  const { styles: proposalStyles } = useProposalPreviewStyles();
  const { message } = useAppMessage();
  const { showLoginModal } = useEmbeddedAuth();
  const currentUser = getCurrentUser();
  // Preview modal state (same as FileUploadList)
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewType, setPreviewType] = useState('image');

  // MD文件创建相关状态
  const [templateSelectorVisible, setTemplateSelectorVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  
  // 隐藏文件输入的引用
  const mp4InputRef = React.useRef(null);
  const attachmentInputRef = React.useRef(null);

  // Preview handler (same logic as FileUploadList)
  const handlePreview = (item) => {

    if (!item || !item.url) return;

    const previewUrl = item.url || '';
    setPreviewImage(previewUrl);
    setPreviewTitle(item.title || item.filename || '文件预览');

    // Determine preview type
    if (item.content_type && item.content_type.indexOf('image') === 0) {
      setPreviewType('image');
    } else if (item.content_type && item.content_type.indexOf('video') === 0) {
      setPreviewType('video');
    } else if (
      /\.pdf$/i.test(item.filename) ||
      (item.content_type && item.content_type.indexOf('application') === 0)
    ) {
      setPreviewType('pdf');
    } else if (
      /\.md$/i.test(item.filename) ||
      (item.content_type && item.content_type.indexOf('text') === 0)
    ) {
      setPreviewType('markdown');
    } else {
      setPreviewType('image'); // Default
    }

    setPreviewVisible(true);
  };

  // 检查是否有文件
  const hasFiles = allItems && allItems.length > 0;

  // 检查是否有MP4文件
  const mp4Count = hasFiles
    ? allItems.filter(
      item =>
        item.content_type === 'video/mp4' ||
        (item.filename && item.filename.toLowerCase().endsWith('.mp4'))
    ).length
    : 0;
  const hasMP4File =
    hasFiles &&
    allItems.some(
      item =>
        item.content_type === 'video/mp4' ||
        (item.filename && item.filename.toLowerCase().endsWith('.mp4'))
    ) &&
    mp4Count >= 5;

  // 检查是否有MD文件
  const mdCount = hasFiles
    ? allItems.filter(
      item =>
        item.content_type === 'markdown' ||
        (item.content_type && item.content_type.indexOf('text') === 0) ||
        (item.filename && item.filename.toLowerCase().endsWith('.md'))
    ).length
    : 0;
  const hasMDFile =
    hasFiles &&
    allItems.some(
      item =>
        item.content_type === 'markdown' ||
        (item.content_type && item.content_type.indexOf('text') === 0) ||
        (item.filename && item.filename.toLowerCase().endsWith('.md'))
    ) &&
    mdCount >= 5;

              // 统计图片和PDF的数量
              const imageAndPdfCount = allItems
                ? allItems.filter(
                    item =>
                      (item.content_type && item.content_type.indexOf('image') === 0) ||
                      (item.type === 'image') ||
                      (item.content_type && item.content_type.indexOf('application') === 0) ||
                      item.type === 'pdf' ||
                      (item.filename && /\.pdf$/i.test(item.filename))
                  ).length >= 10
                : false;

  // 处理文件删除
  const handleDeleteFile = (fileIndex) => {
    if (onFileDelete) {
      onFileDelete(fileIndex);
    }
  };

  // 处理文件上传
  const handleFileUpload = (files) => {
    if (onFileUpload) {
      onFileUpload(files);
    }
  };

  // 处理MP4文件上传
  const handleMP4Upload = (files) => {
    if (onFileUpload) {
      onFileUpload(files);
    }
  };

  // 处理MD文件创建
  const handleMDCreate = () => {
    setTemplateSelectorVisible(true);
  };

  // 处理MP4文件选择
  const handleMP4FileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      try {
        // 使用 uploadService 上传文件
        const uploadedFiles = await Promise.all(
          files.map(async (file) => {
            const uploadResult = await uploadService.uploadFile(file);
            
            // 标准化content_type字段
            const normalizeContentType = (type) => {
              if (type === 'application/pdf') return 'pdf';
              if (type && type.startsWith('image/')) return 'image';
              if (type && type.startsWith('video/')) return 'video';
              if (type === 'text/markdown') return 'markdown';
              return type;
            };
            
            return {
              id: uploadResult.id,
              uid: uploadResult.id,
              url: uploadResult.url,
              name: file.name,
              filename: file.name,
              title: file.name,
              type: 'video',
              content_type: normalizeContentType(file.type),
              size: file.size,
              status: 'done',
              originFileObj: file,
            };
          })
        );
        
        handleMP4Upload(uploadedFiles);
        window.message?.success(`成功上传 ${uploadedFiles.length} 个MP4文件`);
      } catch (error) {
        console.error('MP4上传失败:', error);
        window.message?.error('MP4上传失败: ' + (error.message || '未知错误'));
      }
    }
    // 清空input值，允许重复选择同一文件
    event.target.value = '';
  };

  // 处理附件文件选择
  const handleAttachmentFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      try {
        // 使用 uploadService 上传文件
        const uploadedFiles = await Promise.all(
          files.map(async (file) => {
            // 根据文件类型设置不同的上传选项
            const uploadOptions = {
              title: file.name,
              description: `Uploaded ${file.type.startsWith('image/') ? 'image' : 'PDF'}: ${file.name}`,
            };
            
            // 如果是图片，添加alt_text
            if (file.type.startsWith('image/')) {
              uploadOptions.alt_text = `Uploaded image: ${file.name}`;
            }
            
            const uploadResult = await uploadService.uploadFile(file, uploadOptions);
            
            // 标准化content_type字段
            const normalizeContentType = (type) => {
              if (type === 'application/pdf') return 'pdf';
              if (type && type.startsWith('image/')) return 'image';
              if (type && type.startsWith('video/')) return 'video';
              if (type === 'text/markdown') return 'markdown';
              return type;
            };
            
            return {
              id: uploadResult.id,
              uid: uploadResult.id,
              url: uploadResult.url,
              name: file.name,
              filename: file.name,
              title: file.name,
              type: file.type.indexOf('image') === 0 ? 'image' : 'pdf',
              content_type: normalizeContentType(file.type),
              size: file.size,
              status: 'done',
              originFileObj: file,
            };
          })
        );
        
        handleFileUpload(uploadedFiles);
        window.message?.success(`成功上传 ${uploadedFiles.length} 个附件`);
      } catch (error) {
        console.error('附件上传失败:', error);
        window.message?.error('附件上传失败: ' + (error.message || '未知错误'));
      }
    }
    // 清空input值，允许重复选择同一文件
    event.target.value = '';
  };

  // 统一上传按钮菜单项
  const getUploadMenuItems = () => {
    const items = [];
    
    // MP4上传选项
    if (!hasMP4File) {
      items.push({
        key: 'mp4',
        label: (
          <div className="flex items-center gap-2">
            <VideoCameraOutlined />
            上传视频
          </div>
        ),
        onClick: () => {
          // 触发隐藏的文件输入
          mp4InputRef.current?.click();
        }
      });
    }

    // MD创建选项
    if (!hasMDFile) {
      items.push({
        key: 'md',
        label: (
          <div className="flex items-center gap-2">
            <FileAddOutlined />
            创建MD
          </div>
        ),
        onClick: handleMDCreate
      });
    }

    // 附件上传选项
    if (!imageAndPdfCount) {
      items.push({
        key: 'attachments',
        label: (
          <div className="flex items-center gap-2">
            <FolderAddOutlined />
            上传附件
          </div>
        ),
        onClick: () => {
          // 触发隐藏的文件输入
          attachmentInputRef.current?.click();
        }
      });
    }

    return items;
  };

  // 渲染统一上传按钮
  const renderUnifiedUploadButton = () => {
    const menuItems = getUploadMenuItems();
    
    if (menuItems.length === 0) {
      return null; // 如果没有可用选项，不显示按钮
    }

    return (
      <div className={`${styles.contentItem}  flex items-center justify-center`}>
        <Dropdown
          menu={{ items: menuItems }}
          placement="topCenter"
          trigger={['click']}
        >
          <button
            className="flex flex-col items-center justify-center h-20 w-full "
            disabled={isSubmitting}
          >
            <div className="text-center">
              <div className="text-lg mb-1">
                <PlusOutlined />
              </div>
              <div className="text-sm">上传</div>
            </div>
          </button>
        </Dropdown>
        
        {/* 隐藏的文件输入 */}
        <input
          ref={mp4InputRef}
          type="file"
          accept="video/mp4"
          multiple
          style={{ display: 'none' }}
          onChange={handleMP4FileChange}
          disabled={isSubmitting}
        />
        <input
          ref={attachmentInputRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleAttachmentFileChange}
          disabled={isSubmitting}
        />
      </div>
    );
  };

  // 处理模板选择
  const handleTemplateSelect = (template) => {
    setTemplateSelectorVisible(false);
    setEditingDocument({
      title: template.name,
      content: template.content,
      template_id: template.id,
    });
    setDocumentModalVisible(true);
  };

  // 获取附件内容，支持URL参数
  const getAttachmentContent = async (attachmentId, attachment = null) => {
    try {
      let token = null;

      // 如果提供了attachment对象，尝试从URL中提取token参数
      if (attachment && attachment.url) {
        try {
          const url = new URL(attachment.url);
          token = url.searchParams.get('img_token');
        } catch (e) {
          console.warn('解析URL参数失败:', e);
        }
      }

      // 使用attachmentService获取内容，保留URL参数
      const response = await attachmentService.downloadAttachment(attachmentId, token);
      return response;
    } catch (error) {
      console.error('获取附件内容失败:', error);
      throw error;
    }
  };

  // 处理Markdown编辑
  const handleMarkdownEdit = async (currentItem) => {
    try {

      // 优先使用fields.content，如果不存在则通过API获取
      let content = '';
      if (currentItem.fields?.content) {
        content = currentItem.fields.content;
      } else if (currentItem.content) {
        // 如果fields.content不存在，但currentItem.content存在，使用它
        content = currentItem.content;
      } else {
        // 使用attachmentService获取内容，避免CORS问题
        const response = await getAttachmentContent(currentItem.id, currentItem);

        // 将Blob转换为文本内容
        if (response instanceof Blob) {
          content = await response.text();
        } else {
          content = response;
        }
      }

      // 解析metadata
      let metadata = {};
      try {
        metadata = JSON.parse(currentItem.metadata || '{}');
      } catch (e) {
        console.warn('解析metadata失败:', e);
      }

      // 构建完整的editingDocument对象，确保包含最新的content
      const editingDoc = {
        id: currentItem.id,
        title: metadata.title || currentItem.title || currentItem.filename?.replace(/\.[^/.]+$/, '') || '未命名文档',
        content: content,
        // 传递完整的fields结构，并确保包含最新的content
        fields: {
          ...currentItem.fields,
          content: content, // 确保fields.content是最新的
          name: currentItem.filename || currentItem.file_name || currentItem.title,
          file_name: currentItem.filename || currentItem.file_name,
          filename: currentItem.filename || currentItem.file_name,
          mime_type: currentItem.fields?.mime_type || 'text/markdown',
          content_type: currentItem.fields?.content_type || 'markdown',
          description: currentItem.fields?.description || metadata.title || currentItem.title,
          status: currentItem.fields?.status || 'published',
          file_size: currentItem.size,
          editable: currentItem.fields?.editable !== false // 默认可编辑
        },
        readOnly: false,
        filename: currentItem.filename || currentItem.file_name,
        file_name: currentItem.file_name || currentItem.filename,
        content_type: currentItem.content_type,
        size: currentItem.size,
        inserted_at: currentItem.inserted_at,
        metadata: currentItem.metadata,
        url: currentItem.url,
        inserted_at: currentItem.inserted_at,
        updated_at: currentItem.updated_at,
        user_id: currentItem.user_id,
        attachable_id: currentItem.attachable_id,
        attachable_type: currentItem.attachable_type,
        editable: currentItem.editable
      };

      setEditingDocument(editingDoc);
      setDocumentModalVisible(true);
    } catch (error) {
      console.error('获取Markdown内容失败:', error);
      window.message?.error('获取文档内容失败，请重试');
    }
  };

  // 处理文档保存
  const handleDocumentSave = async (content, title, isMarkdownEditor = true, savedDocument = null) => {
    try {
      if (savedDocument) {
        // 检查是否是更新现有文档还是创建新文档
        if (editingDocument?.id) {
          // 更新现有文档 - 使用传入的最新编辑内容，而不是接口返回的旧数据
          
          const mdFile = {
            id: savedDocument.id,
            type: savedDocument.fields?.mime_type || savedDocument.content_type,
            content_type: savedDocument.fields?.content_type || savedDocument.content_type,
            filename: savedDocument.fields?.name || savedDocument.file_name,
            file_name: savedDocument.fields?.name || savedDocument.file_name,
            title: savedDocument.fields?.name || savedDocument.file_name,
            content: content, // 使用最新的编辑内容
            size: savedDocument.fields?.file_size || savedDocument.size,
            metadata: savedDocument.fields?.metadata || savedDocument.metadata,
            url: savedDocument.fields?.file_url || savedDocument.url,    
            user_id: savedDocument.author_id,
            editable: savedDocument.fields?.editable || savedDocument.editable,
            // 确保传递完整的fields结构，并更新content字段
            fields: {
              ...savedDocument.fields,
              size: savedDocument.fields?.file_size || savedDocument.size,
              filename: savedDocument.fields?.name || savedDocument.file_name,
              file_name: savedDocument.fields?.name || savedDocument.file_name,
              title: savedDocument.fields?.name || savedDocument.file_name,
              content: content, // 使用最新的编辑内容
              // 确保所有必要的字段都存在
              mime_type: savedDocument.fields?.mime_type || 'text/markdown',
              content_type: savedDocument.fields?.content_type || 'markdown',
              description: savedDocument.fields?.description || title,
              status: savedDocument.fields?.status || 'published',
            }
          };

          if (onFileUpdate) {
            onFileUpdate(mdFile);
          }
        } else {
          // 创建新文档 - 使用接口返回的数据
          
          const mdFile = {
            id: savedDocument.id,
            type: savedDocument.fields?.mime_type || savedDocument.content_type,
            content_type: savedDocument.fields?.content_type || savedDocument.content_type,
            filename: savedDocument.fields?.name || savedDocument.file_name,
            file_name: savedDocument.fields?.name || savedDocument.file_name,
            title: savedDocument.fields?.name || savedDocument.file_name,
            content: savedDocument.fields?.content || savedDocument.content, // 新建时使用接口返回的内容
            size: savedDocument.fields?.file_size || savedDocument.size,
            metadata: savedDocument.fields?.metadata || savedDocument.metadata,
            url: savedDocument.fields?.file_url || savedDocument.url,    
            user_id: savedDocument.author_id,
            editable: savedDocument.fields?.editable || savedDocument.editable,
            // 确保传递完整的fields结构
            fields: savedDocument.fields || {}
          };

          if (onFileUpload) {
            onFileUpload([mdFile]);
          }
        }
      } else {
        // 原有的保存逻辑（兼容旧逻辑）
        if (editingDocument?.id) {
          // 更新现有文档
          const filename = generateDocumentFilename(title);
          const markdownFile = markdownToFile(content, filename);

          // 将文件内容转换为base64
          const fileContent = await blobToMarkdown(markdownFile);
          const base64Content = btoa(unescape(encodeURIComponent(fileContent)));
          

          // 使用附件更新API
          const updatedAttachment = await attachmentService.updateAttachment(editingDocument.id, {
            file_content: base64Content,
            file_name: filename,
            description: `Markdown文档: ${title}`,
            status: 'published',
            content_type: 'markdown',
          });

          // 更新附件列表中的文件
          const updatedFile = {
            id: updatedAttachment.id,
            type: 'markdown',
            content_type: 'markdown',
            filename: updatedAttachment.filename,
            title: updatedAttachment.filename,
            content: content,
            size: updatedAttachment.size,
            inserted_at: updatedAttachment.inserted_at,
            metadata: updatedAttachment.metadata,
            url: updatedAttachment.url,
            inserted_at: updatedAttachment.inserted_at,
            updated_at: updatedAttachment.updated_at,
            user_id: updatedAttachment.user_id,
            attachable_id: updatedAttachment.attachable_id,
            attachable_type: updatedAttachment.attachable_type,
            editable: updatedAttachment.editable
          };

          // 使用onFileUpdate更新现有文件
          if (onFileUpdate) {
            onFileUpdate(updatedFile);
          }
        } else {
          // 创建新文档
          const filename = generateDocumentFilename(title);
          const markdownFile = markdownToFile(content, filename);

          // 使用附件上传API，根据是否使用Markdown编辑器设置editable字段
          const uploadedAttachment = await attachmentService.uploadAttachment(markdownFile, {
            description: `Markdown文档: ${title}`,
            editable: isMarkdownEditor, // 根据是否使用Markdown编辑器设置
            status: 'published',
            metadata: {
              source: isMarkdownEditor ? 'markdown_editor' : 'other_upload',
              category: 'document',
              title: title,
              template_id: editingDocument?.template_id,
              content_length: content.length,
              inserted_at: new Date().toISOString()
            }
          });

          // 将MD文件添加到附件列表
          const mdFile = {
            id: uploadedAttachment.id,
            type: 'markdown',
            content_type: 'markdown',
            filename: uploadedAttachment.filename,
            title: uploadedAttachment.filename,
            content: content,
            size: uploadedAttachment.size,
            inserted_at: uploadedAttachment.inserted_at,
            metadata: uploadedAttachment.metadata,
            url: uploadedAttachment.url,
            inserted_at: uploadedAttachment.inserted_at,
            updated_at: uploadedAttachment.updated_at,
            user_id: uploadedAttachment.user_id,
            attachable_id: uploadedAttachment.attachable_id,
            attachable_type: uploadedAttachment.attachable_type,
            editable: uploadedAttachment.editable
          };

          if (onFileUpload) {
            onFileUpload([mdFile]);
          }
        }
      }

      // 关闭Modal
      setDocumentModalVisible(false);
      setEditingDocument(null);
    } catch (error) {
      console.error('保存MD文档失败:', error);
      throw error;
    }
  };

  // 渲染上传说明文本组件
  const renderUploadHint = () => (
    <div className="text-xs text-gray-500 mt-2 text-center">
      暂无视频/Markdown；点击"开始编辑"立即上传，帮助AI和社区更好地理解你的提案
    </div>
  );


  // 渲染空白上传组件（仅显示UI，不处理上传）
  const renderEmptyUpload = () => {
    return (
      <div className="w-full h-96 flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
        <div className="text-5xl text-blue-500 mb-4">
          <FileOutlined className="text-2xl" />
        </div>
        <div className="text-base text-blue-500 font-medium">请上传文档</div>
        {renderUploadHint()}
      </div>
    );
  };





  // 获取背景色类名
  const getBackgroundClass = () => {
    return optimizeMode
      ? 'bg-[var(--color-assist-6)]'
      : 'bg-[var(--color-assist-4)]';
  };

  // 获取项目类型（做健壮化兼容）
  const getItemType = (item) => {
    const ct = ((item?.content_type) || '').toString().toLowerCase().trim();
    const fn = ((item?.filename || item?.file_name || '')).toString().toLowerCase().trim();

    if (ct.startsWith('image') || item?.type === 'image') return 'image';
    if (ct === 'markdown' || ct.includes('markdown') || fn.endsWith('.md')) return 'markdown';
    if (ct.startsWith('video') || item?.type === 'video' || fn.endsWith('.mp4') || fn.endsWith('.mov') || fn.endsWith('.webm') || fn.endsWith('.avi') || fn.endsWith('.ogg')) return 'video';
    if (ct === 'application/pdf' || ct === 'pdf' || fn.endsWith('.pdf') || item?.type === 'pdf') return 'pdf';

    // 回退
    return item?.type || (ct.split('/')[0] || 'document');
  };


  // 检查是否为选中项目
  const isSelectedItem = (item, globalIndex) => {
    return (
      selectedItem.type === getItemType(item) &&
      selectedItem.index === globalIndex
    );
  };

  // 渲染文件列表
  const renderFileList = () => {
    // 分类文件
    const categorizedFiles = categorizeFiles();
    
    // 渲染分组和分割线
    return renderCategorizedFiles(categorizedFiles);
  };

  // 分类文件
  const categorizeFiles = () => {
    const videos = [];
    const mds = [];
    const pdfs = [];
    const images = [];
    
    allItems.forEach((item, globalIndex) => {
      if (isVideoFile(item)) {
        videos.push({ item, globalIndex });
      } else if (isMarkdownFile(item)) {
        mds.push({ item, globalIndex });
      } else if (isPdfFile(item)) {
        pdfs.push({ item, globalIndex });
      } else if (isImageFile(item)) {
        images.push({ item, globalIndex });
      }
    });

    return { videos, mds, pdfs, images };
  };

  // 文件类型判断函数
  const isVideoFile = (item) => {
    const ct = ((item?.content_type) || '').toString().toLowerCase().trim();
    const fn = ((item?.filename || item?.file_name || '')).toString().toLowerCase().trim();
    return item?.type === 'video' || ct.startsWith('video') || fn.endsWith('.mp4') || fn.endsWith('.mov') || fn.endsWith('.webm') || fn.endsWith('.avi') || fn.endsWith('.ogg');
  };
  
  const isMarkdownFile = (item) => {
    const ct = ((item?.content_type) || '').toString().toLowerCase().trim();
    const fn = ((item?.filename || item?.file_name || '')).toString().toLowerCase().trim();
    return ct === 'markdown' || ct.includes('markdown') || ct.startsWith('text/markdown') || fn.endsWith('.md');
  };
  
  const isPdfFile = (item) => {
    const ct = ((item?.content_type) || '').toString().toLowerCase().trim();
    const fn = ((item?.filename || item?.file_name || '')).toString().toLowerCase().trim();
    return item?.type === 'pdf' || ct === 'application/pdf' || ct === 'pdf' || fn.endsWith('.pdf');
  };
  
  const isImageFile = (item) => {
    const ct = ((item?.content_type) || '').toString().toLowerCase().trim();
    return item?.type === 'image' || ct.startsWith('image');
  };

  // 渲染分类后的文件
  const renderCategorizedFiles = ({ videos, mds, pdfs, images }) => {
    const result = [];
    
    // 按顺序渲染各组文件
    if (videos.length > 0) {
      result.push(...renderFileItems(videos));
      result.push(<Divider key="divider-video" />);
    } else {
      // 没有MP4文件时显示提示
      result.push(renderEmptyCategory('暂无视频', 'video'));
      result.push(<Divider key="divider-video-empty" />);
    }
    
    if (mds.length > 0) {
      result.push(...renderFileItems(mds));
      result.push(<Divider key="divider-md" />);
    } else {
      // 没有MD文件时显示提示
      result.push(renderEmptyCategory('暂无MD', 'document'));
      result.push(<Divider key="divider-md-empty" />);
    }
    
    // PDF和图片合并为附件分类
    const hasAttachments = pdfs.length > 0 || images.length > 0;
    if (hasAttachments) {
      if (pdfs.length > 0) {
        result.push(...renderFileItems(pdfs));
      }
      if (images.length > 0) {
        result.push(...renderFileItems(images));
      }
      // 附件分类后有分割线
      result.push(<Divider key="divider-attachments" />);
    } else {
      // 没有附件时显示提示
      result.push(renderEmptyCategory('暂无附件', 'attachment'));
      result.push(<Divider key="divider-attachments-empty" />);
    }
    
    // 移除最后一个分割线
    return removeLastDivider(result);
  };

  // 渲染文件项
  const renderFileItems = (fileArray) =>
    fileArray.map(({ item, globalIndex }) => {
      const itemType = getItemType(item);
      const isSelected = isSelectedItem(item, globalIndex);
      
      return (
        <div
          key={`${itemType}-${globalIndex}`}
          onClick={() => setSelectedItem({ type: itemType, index: globalIndex })}
          className={`${styles.contentItem} ${getBackgroundClass()} ${isSelected ? 'selected' : ''} relative`}
        >
          {/* 删除按钮 */}
          {renderDeleteButton(globalIndex)}
          
          {/* 文件预览 */}
          <div className="item-image">
            {renderFilePreview(item)}
            <div className={`type-badge ${itemType}`}>
              {getTypeName(itemType)}
            </div>
            {isSelected && renderSelectedOverlay()}
          </div>
          
          {/* 文件信息 */}
          <div className="item-info">
            <div className="item-meta">
              <div className={`meta-icon ${itemType}`}>
                {getIcon(itemType)}
              </div>
              <span className="item-index">{globalIndex + 1}</span>
            </div>
            <div className="item-title">
              {item.title || item.filename}
            </div>
          </div>
        </div>
      );
    });

  // 渲染删除按钮
  const renderDeleteButton = (globalIndex) => {
    if (!isEditMode || isSubmitting) return null;
    
    return (
      <Button
        size="small"
        icon={<CloseOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteFile(globalIndex);
        }}
        className={`absolute top-1 right-1 z-10 w-6 h-6 rounded-full p-0 flex items-center justify-center text-red-500 ${proposalStyles.deleteButton}`}
        disabled={isSubmitting}
      />
    );
  };

  // 渲染文件预览
  const renderFilePreview = (item) => {
    if (isImageFile(item)) {
      return <img src={item.url} alt={item.title || item.filename} />;
    }
    
    if (isVideoFile(item)) {
      return (
        <div className="relative w-full h-full">
          <video
            src={item.url}
            className="w-full h-full object-cover"
            preload="metadata"
            muted
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
            <div className="text-white text-4xl">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      );
    }
    
    if (isPdfFile(item)) {
      return (
        <PDFThumbnail
          url={item.url}
          className="w-full h-full object-cover"
        />
      );
    }
    
    if (isMarkdownFile(item)) {
      return (
        <div className="w-full h-full bg-gray-50 flex items-center justify-center">
          <FileTextOutlined className="text-5xl text-blue-500" />
        </div>
      );
    }
    
    // 其他文件类型（PPT、Word等）
    if (isOfficeFile(item)) {
      return (
        <FileTextOutlined className="text-5xl text-gray-400 w-full h-full flex items-center justify-center" />
      );
    }
    
    return null;
  };

  // 判断是否为Office文件
  const isOfficeFile = (item) => 
    item.type === 'ppt' || item.type === 'word' ||
    item.content_type?.includes('presentation') || 
    item.content_type?.includes('wordprocessing');

  // 渲染选中状态覆盖层
  const renderSelectedOverlay = () => (
    <div className="selected-overlay">
      <div className="selected-icon">
        <EyeOutlined />
      </div>
    </div>
  );

  // 分割线组件
  const Divider = ({ key }) => (
    <div
      key={key}
      className="mx-4 flex flex-col justify-stretch items-center"
      style={{ alignSelf: 'stretch' }}
    >
      <div className="h-full w-0.5 bg-gray-200" style={{ minHeight: '100%' }}></div>
    </div>
  );

  // 渲染空分类提示
  const renderEmptyCategory = (text, type) => (
    <div key={`empty-${type}`} className={`${styles.contentItem} flex items-center justify-center`}>
      <div className="text-center text-gray-400">
        <div className="text-sm">{text}</div>
      </div>
    </div>
  );

  // 移除最后一个分割线
  const removeLastDivider = (result) => {
    if (result.length > 0 && 
        React.isValidElement(result[result.length - 1]) && 
        result[result.length - 1].key && 
        result[result.length - 1].key.startsWith('divider')) {
      result.pop();
    }
    return result;
  };


  return (
    <div className={`${styles.previewSection} ${getBackgroundClass()}`}>
      {/* 主预览区域 */}
      {currentStep === 1 && (
        <div
          className={`${styles.previewArea} relative ${getBackgroundClass()}`}
        >
          <Card
            bordered={false}
            className={`${styles.previewCard} ${getBackgroundClass()}`}
          >
            <div className={proposalStyles.previewImage}>
              {hasFiles ? (
                <>
                  {(() => {
                    const currentItem = getCurrentItem();
                    return currentItem ? renderPreview(currentItem) : (
                      <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-xl">
                        <div className="text-center">
                          <div className="text-6xl text-gray-300 mb-4">📄</div>
                          <div className="text-lg font-medium text-gray-600 mb-2">
                            文件加载中
                          </div>
                          <div className="text-sm text-gray-500">
                            正在准备文件预览
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {!isEditMode && (
                    <div className="preview-actions absolute bottom-28 right-4 flex flex-col gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <Button
                          type="text"
                          icon={
                            proposalData?.is_liked ? (
                              <img
                                src={likeFilledIcon}
                                alt="点赞"
                                className="w-6 h-6"
                                style={{ display: 'block' }}
                              />
                            ) : (
                              <img
                                src={likeIcon}
                                alt="点赞"
                                className="w-6 h-6"
                                style={{ display: 'block' }}
                              />
                            )
                          }
                          className={`w-6 h-6 p-0 ${proposalData?.is_liked ? 'text-red-500' : ''
                            }`}
                          onClick={() => {
                            if (!currentUser) {
                              showLoginModal();
                              return;
                            }
                            onLike?.();
                          }}
                          loading={likeLoading}
                          disabled={likeLoading || !onLike}
                        />
                        <div className="text-xs text-gray-500 leading-none">
                          {proposalData?.interactions.like_count || 0}
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <Button
                          type="text"
                          icon={
                            <img
                              src={commentIcon}
                              alt="评论"
                              className="w-6 h-6"
                              style={{ display: 'block' }}
                            />
                          }
                          className="w-6 h-6 p-0"
                          onClick={() => {
                            setCommentModalVisible((prev) => !prev);
                          }}
                        />
                        <div className="text-xs text-gray-500 leading-none">
                          {proposalData?.interactions?.comment_count || 0}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // 显示空白上传组件 - 使用统一的上传机制
                <div className="p-5">{renderEmptyUpload()}</div>
              )}
            </div>
            {hasFiles && (
              <div className="preview-info">
                <div className="info-left">
                  <div className={`type-icon ${selectedItem.type}`}>
                    {getIcon(selectedItem.type)}
                  </div>
                  <div className="info-text">
                    <div className="title">
                      {getCurrentItem()?.title || getCurrentItem()?.filename || '未命名文件'}
                    </div>
                    {/* <div className="meta">
                      {getTypeName(selectedItem.type)} ·{' '}
                      {selectedItem.index + 1}
                    </div> */}
                  </div>
                </div>
                <div className="info-actions">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const currentItem = getCurrentItem();
                      if (currentItem) {
                        handlePreview(currentItem);
                      }
                    }}
                    disabled={!getCurrentItem()}
                  />
                  {/* Markdown编辑按钮 - 只在编辑模式且为Markdown文件时显示 */}
                  {isEditMode && getCurrentItem() && (
                    (getCurrentItem().content_type === 'text/markdown' ||
                      /\.md$/i.test(getCurrentItem().filename)) && (
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          const currentItem = getCurrentItem();
                          if (currentItem) {
                            handleMarkdownEdit(currentItem);
                          }
                        }}
                        disabled={!getCurrentItem() || isSubmitting}
                        title="编辑Markdown文档"
                      />
                    )
                  )}
                  {/* <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    href={getCurrentItem()?.url}
                    target="_blank"
                  /> */}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
      {/* 横向滚动内容切换区域 */}

      <div className={`${styles.contentGrid} ${getBackgroundClass()}`}>
        <div
          className={`${styles.scrollContainer} ${getBackgroundClass()} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
          ref={scrollContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* 左侧滚动按钮 */}
          {showLeftScroll && (
            <button
              onClick={scrollLeft}
              className="scroll-button scroll-left absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 border border-gray-200 rounded-full w-8 h-8 flex items-center justify-center cursor-pointer shadow-md transition-all duration-200 hover:bg-white hover:shadow-lg"
            >
              <LeftOutlined className="text-xs text-gray-600" />
            </button>
          )}

          {/* 右侧滚动按钮 */}
          {showRightScroll && (
            <button
              onClick={scrollRight}
              className="scroll-button scroll-right absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 border border-gray-200 rounded-full w-8 h-8 flex items-center justify-center cursor-pointer shadow-md transition-all duration-200 hover:bg-white hover:shadow-lg"
            >
              <RightOutlined className="text-xs text-gray-600" />
            </button>
          )}

          <div className={`${styles.itemsContainer} ${getBackgroundClass()}`}>
            {/* Upload functionality at the end of the file list */}
            {/* 统一上传按钮 - 在编辑模式下显示 */}
            {isEditMode && !isSubmitting && renderUnifiedUploadButton()}
            {renderFileList()}


          </div>
        </div>
      </div>

      {/* add a line of text "支持上传PDF（不大于5M，不超过10页）、图片（不大于5M）或Markdown（不大于5M），最多可以上传8个附件" */}
      {currentStep === 1 && (
        <div className="text-xs text-gray-500 mt-2 text-center">
          支持上传视频（MP4格式，不超过5个）、创建Markdown文档（不超过5个）、以及其他附件（支持PDF和图片，不超过10个）
        </div>
      )}

      {/* 通用文件预览Modal */}
      <FilePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        file={(() => {
          const currentItem = getCurrentItem();
          if (currentItem) {
            return {
              ...currentItem,
            };
          }
          return {
            content_type: previewType === 'video' ? 'video/mp4' :
              previewType === 'pdf' ? 'application/pdf' :
                previewType === 'markdown' ? 'text/markdown' : 'image/jpeg',
            filename: previewTitle,
        
          };
        })()}
      />

      {/* 模板选择器 */}
      <TemplateSelector
        visible={templateSelectorVisible}
        onClose={() => setTemplateSelectorVisible(false)}
        onSelect={handleTemplateSelect}
      />

      {/* 文档编辑Modal */}
      <DocumentModal
        visible={documentModalVisible}
        onClose={() => {
          setDocumentModalVisible(false);
          setEditingDocument(null);
        }}
        initialDocument={editingDocument}
        onSave={handleDocumentSave}
        attachments={allItems}
        displayFiles={[]}
      />
    </div>
  );
};

export default ProposalPreview;
